import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useAxios } from "../../hooks";
import { LoadingButton } from "../../components";
import { RiDeleteBin6Line } from "react-icons/ri";
import { convertDate } from "../../utils";

// Styled Components
const RecordCard = styled(Box)`
  padding: 10px;
  background: #ffffff;
  border: 1px solid #ddd;
  border-radius: 8px;
  transition: box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "transparent",
    width: "calc(100% - 15px)",
    margin: "7.5px",
  },
}));

const DateHeader = styled(Typography)`
  font-size: 16px;
  font-weight: bold;
  color: #424242;
  margin-bottom: 8px;
`;

const ContentBox = styled(Box)(({ theme }) => ({
  textAlign: "start",
  alignContent: "center",
  alignItems: "center",
  borderRadius: "12.5px",
  background: "rgb(255, 255, 255)",
  padding: "17.5px",
  width: "100%",
}));

const DeleteDialogBox = styled(Box)(({ theme }) => ({
  textAlign: "start",
  alignItems: "center",
  borderRadius: "12.5px",
  background: "rgb(255, 255, 255)",
  padding: "25px",
  width: "100%",
}));

// Main Component
const ConsentListDialog = ({ consentListPopUp, setConsentListPopUp }) => {
  const axios = useAxios();
  const { visitorData } = useSelector((state) => state.memberDetails);
  const [consent, setConsent] = useState([{}]);
  const [deleteDetailsPopUp, setDeleteDetailsPopUp] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);

  useEffect(() => {
    axios({
      url: `admin/profile/get-user-bookings?phone_number=${visitorData.phoneNumber}&consent_bookings=true`,
      disableRedirect: true,
    }).then((response) => {
      if (response.status) {
        setConsent(response.data);
      }
    });
  }, [deleteDetailsPopUp]);

  const handleDeleteData = async () => {
    try {
      axios({
        url: `event/visitor/revoke-consent`,
        method: "POST",
        data: { booking_id: selectedConsent },
      }).then((response) => {
        if (response.status) {
        }
        setDeleteDetailsPopUp(false);
      });
    } catch (error) {
      console.error("Error deleting consent", error);
    }
  };

  return (
    <>
      <StyledDialog
        open={consentListPopUp}
        onClose={() => setConsentListPopUp(false)}
        maxWidth="sm"
        fullWidth
      >
        <Grid container sx={{ background: "transparent", padding: "10px 0px" }}>
          <Grid item container justifyContent={"end"}>
            <CloseIcon
              sx={{
                fontSize: "35px",
                padding: "4px",
                color: "white",
                borderRadius: "50%",
                background: "#FFFFFF38",
              }}
              onClick={() => setConsentListPopUp(false)}
            />
          </Grid>
        </Grid>

        <DialogContent sx={{ padding: "5px 0px" }}>
          <ContentBox>
            <DateHeader>Consent List</DateHeader>
            <Fragment>
              {consent.length != 0 ? (
                <Fragment>
                  {" "}
                  {consent.map((item, index) => (
                    <Fragment key={index}>
                      <DateHeader></DateHeader>
                      <RecordCard>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={8}>
                            <Typography variant="body2" fontWeight={900}>
                              Given For:{" "}
                              {item?.campus_name ||
                                item?.event_name ||
                                item?.premises_name ||
                                item?.hotel_name}
                            </Typography>
                            <Typography variant="body2" fontWeight={900}>
                              Given Date: {convertDate(item?.created_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} textAlign="right">
                            <LoadingButton
                              variant="contained"
                              style={{
                                fontSize: "12px",
                                padding: "5px",
                                background: "red",
                              }}
                              onClick={() => {
                                setSelectedConsent(
                                  item?.appointment_id ||
                                    item?.booking_id ||
                                    item?.student_id
                                );
                                setDeleteDetailsPopUp(true);
                              }}
                            >
                              Revoke
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </RecordCard>
                    </Fragment>
                  ))}
                </Fragment>
              ) : (
                <Fragment>
                  {" "}
                  <RecordCard>
                    <Typography
                      variant="body1"
                      textAlign={"center"}
                      fontWeight={900}
                    >
                      No Consent Found
                    </Typography>
                  </RecordCard>
                </Fragment>
              )}
            </Fragment>
          </ContentBox>
        </DialogContent>
      </StyledDialog>

      <StyledDialog
        open={deleteDetailsPopUp}
        onClose={() => setDeleteDetailsPopUp(false)}
        maxWidth="sm"
        fullWidth
      >
        <Grid container sx={{ background: "transparent", padding: "10px 0px" }}>
          <Grid item container justifyContent={"end"}>
            <CloseIcon
              sx={{
                fontSize: "35px",
                padding: "4px",
                color: "white",
                borderRadius: "50%",
                background: "#FFFFFF38",
              }}
              onClick={() => setDeleteDetailsPopUp(false)}
            />
          </Grid>
        </Grid>

        <DialogContent sx={{ padding: "5px 0px" }}>
          <DeleteDialogBox>
            <RiDeleteBin6Line
              style={{
                width: "55px",
                height: "55px",
                border: "1px solid #FDE9E9",
                background: "#FDE9E9",
                borderRadius: "50%",
                padding: "16px",
                color: "#942D2D",
                margin: "0px 0px 10px 0px",
              }}
            />
            <Typography
              variant="h6"
              fontWeight={700}
              textAlign={"left"}
              sx={{
                "-webkit-text-stroke-width": "0.1px",
                fontSize: "20px",
                marginBottom: "5px",
              }}
            >
              Confirm Deletion
            </Typography>
            <Typography
              variant="h6"
              fontWeight={400}
              textAlign={"left"}
              sx={{ marginTop: 2, color: "#111111CC", marginBottom: "20px" }}
            >
              You are about to permanently delete all details associated with
              this consent.
            </Typography>

            <Typography
              variant="h6"
              fontWeight={400}
              textAlign={"left"}
              sx={{
                fontSize: "12px",
                color: "#111111CC",
                margin: "0px",
                background: "#F8F8F8",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              Note: This action cannot be undone.
            </Typography>

            <Typography
              variant="h6"
              fontWeight={400}
              textAlign={"left"}
              sx={{ marginTop: 2, color: "#111111CC", marginBottom: "20px" }}
            >
              Are you sure you want to continue?
            </Typography>

            <Grid container justifyContent={"end"}>
              <LoadingButton
                onClick={() => setDeleteDetailsPopUp(false)}
                style={{
                  width: "45%",
                  marginTop: "20px",
                  padding: "12px 16px",
                  background: "white",
                  color: "#3936EF",
                  border: "0.6px solid #0000001A",
                  fontSize: "14px",
                }}
              >
                No, Cancel
              </LoadingButton>

              <LoadingButton
                onClick={handleDeleteData}
                style={{
                  width: "45%",
                  marginTop: "20px",
                  padding: "12px 16px",
                  marginLeft: "10px",
                  fontSize: "14px",
                }}
              >
                Yes, Delete
              </LoadingButton>
            </Grid>
          </DeleteDialogBox>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default ConsentListDialog;
